<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import { useSubAppStore } from '@/store/sub-app.store.ts'
  import SearchableList from '@/components/SearchableList.vue'
  import List from '@/components/List.vue'
  import ListItem from '@/components/ListItem.vue'
  import { InputField, UiButton, require } from '@logistiek-zonder-papier/lzp-library'
  import { useSearch } from '@/composables/use-search.ts'
  import { storeToRefs } from 'pinia'
  import { useRouter } from 'vue-router'
  import { useCustomerStore } from '@/store/customer.store'
  import { type SubApp } from '@/types/sub-app-types'
  import TopBar from '@/components/TopBar.vue'

  const customerStore = useCustomerStore()
  const router = useRouter()
  const subAppStore = useSubAppStore()
  const { subApps } = storeToRefs(subAppStore)
  const { searchTerm, filteredItems: filteredSubApps } = useSearch<SubApp>(subApps)

  const loading = ref(false)
  const error = ref<string | null>(null)

  onMounted(async () => {
    loading.value = true
    error.value = null

    try {
      await subAppStore.fetchSubApps()
      await customerStore.fetchCustomers()
    } catch {
      error.value = 'The list could not be retrieved'
    } finally {
      loading.value = false
    }
  })

  async function onCreate() {
    await router.push({ name: 'sub-app-details', params: { subAppId: 'new' } })
  }

  async function onDelete(subApp: SubApp) {
    await subAppStore.deleteSubApp(require(subApp.id))
  }

  async function onSelect(subApp: SubApp) {
    await router.push({
      name: 'sub-app-details',
      params: { subAppId: subApp.id },
    })
  }
</script>

<template>
  <TopBar />
  <SearchableList>
    <template #header>
      <div class="header">
        <h2>Organisations</h2>
        <UiButton icon="plus" variant="prominent" @click="onCreate()" />
      </div>
      <InputField
        :autoFocus="true"
        v-model="searchTerm"
        type="search"
        placeholder="Search organisation"
      />
    </template>
    <template #default>
      <List
        :items="filteredSubApps"
        :loading="loading"
        :error="error"
        :searchTerm="searchTerm"
        @select="onSelect"
      >
        <template #item="{ item: subApp }">
          <ListItem
            :label="subApp.name"
            :imageSrc="subApp.imageSrc"
            :onClick="() => onDelete(subApp)"
          />
        </template>
      </List>
    </template>
  </SearchableList>
</template>

<style scoped lang="css">
  .header {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }
</style>
